import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  createLessonAPIEndpoint,
  EndPoints,
} from "../../../services/LessonServices";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { VpnKeyRounded } from "@mui/icons-material";
import { FaArrowLeft, FaBookOpen, FaEye, FaFire, FaLock } from "react-icons/fa";

function LessonDetails() {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    createLessonAPIEndpoint(EndPoints.lessonDetails)
      .fetchById(id)
      .then((res) => {
        setLesson(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching lesson details:", err);
        setLoading(false);
      });
  }, [id]);

  const handleEnroll = async () => {
    try {
      // =========================
      // FREE LESSON
      // =========================
      if (lesson.isFree) {
        await createLessonAPIEndpoint(EndPoints.enroll).postWithToken({
          lessonId: lesson.id,
          accessCode: null,
        });

        sessionStorage.setItem("startFullscreen", "true");
        navigate(`/lessons/watch/${lesson.id}`);

        return;
      }

      // =========================
      // ALREADY HAS ACCESS
      // =========================
      if (lesson.canAccess) {
        sessionStorage.setItem("startFullscreen", "true");
        navigate(`/lessons/watch/${lesson.id}`);

        return;
      }

      // =========================
      // NEED ACCESS CODE
      // =========================
      setShowCodeDialog(true);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Unable to access this lesson.",
        {
          variant: "error",
        },
      );
    }
  };
  const handelCodeSubmit = async () => {
    try {
      await createLessonAPIEndpoint(EndPoints.enroll).postWithToken({
        lessonId: lesson.id,
        accessCode: accessCode,
      });
      enqueueSnackbar("Access code submitted successfully!", {
        variant: "success",
        style: { fontSize: "16px" },
      });
      setShowCodeDialog(false);
      navigate(`/my-lessons`);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error submitting access code",
        {
          variant: "error",
          style: { fontSize: "16px" },
        },
      );
      return;
    }
  };

  if (loading)
    return (
      <section className="section">
        <CircularProgress
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            translate: "transform(-50%, -50%)",
          }}
        />
      </section>
    );

  if (!lesson)
    return (
      <section className="lesson-details section">Lesson not found</section>
    );

  return (
    <section className="lesson-details section">
      {" "}
      <div className="lesson-details-container">
        {" "}
        {/* ========================= HEADER ========================= */}{" "}
        <div className="details-header">
          {" "}
          <span className="details-badge">
            {" "}
            <FaBookOpen /> English Lesson{" "}
          </span>{" "}
          <h1>{lesson.title}</h1>{" "}
          <p>
            {" "}
            {lesson.description ||
              "ابدأ الدرس واتعلم بطريقة سهلة ومبسطة مع PureEng."}{" "}
          </p>{" "}
        </div>{" "}
        {/* ========================= MAIN CARD ========================= */}{" "}
        <Card className="details-card">
          {" "}
          <CardContent className="details-card-content">
            {" "}
            {/* Lesson visual */}{" "}
            <div className="details-visual">
              {" "}
              <div className="details-brand"> PureEng </div>{" "}
              <div className="details-visual-icon">
                {" "}
                <FaBookOpen />{" "}
              </div>{" "}
              <span className="details-visual-label"> ENGLISH </span>{" "}
            </div>{" "}
            {/* Lesson information */}{" "}
            <div className="lesson-info">
              {" "}
              <div className="info-title">
                {" "}
                <h2>معلومات الدرس</h2>{" "}
                <span> Lesson #{String(lesson.id).padStart(2, "0")} </span>{" "}
              </div>{" "}
              {/* Views */}{" "}
              <div className="views-grid">
                {" "}
                <div className="view-box">
                  {" "}
                  <div className="view-icon">
                    {" "}
                    <FaEye />{" "}
                  </div>{" "}
                  <div>
                    {" "}
                    <span>الحد الأقصى للمشاهدة</span>{" "}
                    <strong> {lesson.maxViewPerStudent ?? 0} </strong>{" "}
                  </div>{" "}
                </div>{" "}
                <div
                  className={`view-box ${lesson.remainingViews > 0 ? "available" : "finished"}`}
                >
                  {" "}
                  <div className="view-icon">
                    {" "}
                    <FaFire />{" "}
                  </div>{" "}
                  <div>
                    {" "}
                    <span>المشاهدات المتبقية</span>{" "}
                    <strong> {lesson.remainingViews ?? 0} </strong>{" "}
                  </div>{" "}
                </div>{" "}
              </div>{" "}
              {/* Warning */}{" "}
              {lesson.remainingViews === 0 && (
                <div className="views-warning">
                  {" "}
                  <FaLock />{" "}
                  <span> لقد انتهت المشاهدات المتاحة لهذا الدرس. </span>{" "}
                </div>
              )}{" "}
            </div>{" "}
          </CardContent>{" "}
        </Card>{" "}
        {/* ========================= ACTION ========================= */}{" "}
        <div className="lesson-action">
          {" "}
          <Button
            className="lesson-main-button"
            variant="contained"
            onClick={handleEnroll}
          >
            {" "}
            {lesson.canAccess ? (
              <>
                {" "}
                ابدأ الدرس <FaArrowLeft />{" "}
              </>
            ) : (
              <>
                {" "}
                أدخل كود الدخول <VpnKeyRounded />{" "}
              </>
            )}{" "}
          </Button>{" "}
          <p>
            {" "}
            {lesson.canAccess
              ? "جاهز؟ ابدأ الدرس دلوقتي 🚀"
              : "عندك كود دخول؟ استخدمه للوصول للدرس"}{" "}
          </p>{" "}
        </div>{" "}
        {/* ========================= ACCESS CODE DIALOG ========================= */}{" "}
        <Dialog
          open={showCodeDialog}
          onClose={() => setShowCodeDialog(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ className: "access-dialog" }}
        >
          {" "}
          {/* Header */}{" "}
          <div className="dialog-header">
            {" "}
            <div className="dialog-icon">
              {" "}
              <VpnKeyRounded />{" "}
            </div>{" "}
            <h2>كود الدخول</h2>{" "}
            <p> .أدخل الكود اللي استلمته علشان تقدر تدخل الدرس </p>{" "}
          </div>{" "}
          {/* Content */}{" "}
          <DialogContent className="dialog-content">
            {" "}
            <TextField
              label="كود الدخول"
              placeholder="اكتب الكود هنا"
              variant="outlined"
              fullWidth
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              autoFocus
              className="access-input"
            />{" "}
            <p className="input-hint">
              {" "}
              .تأكد إنك كتبت الكود بالضبط زي ما استلمته
            </p>{" "}
          </DialogContent>{" "}
          {/* Actions */}{" "}
          <DialogActions className="dialog-actions">
            {" "}
            <Button
              onClick={() => {
                setShowCodeDialog(false);
                setAccessCode("");
              }}
              className="cancel-button"
            >
              {" "}
              إلغاء{" "}
            </Button>{" "}
            <Button
              onClick={handelCodeSubmit}
              variant="contained"
              disabled={!accessCode.trim()}
              className="submit-button"
            >
              {" "}
              دخول للدرس{" "}
            </Button>{" "}
          </DialogActions>{" "}
        </Dialog>{" "}
      </div>{" "}
    </section>
  );
}

export default LessonDetails;
